import { TFunction } from "i18next/typescript/t";
import * as yup from "yup";
import { FieldPath } from "react-hook-form";

import {
  Honorifics,
  MAX_PROFILE_PICTURE_FILE_SIZE,
  SUPPORTED_PROFILE_PICTURE_FILE_FORMATS,
} from "@/shared/utils/constants";
import { AdminRegisterFormValues, RegisterFormValues } from "../types";

const registerAccountProfileSchema = (t: TFunction) => ({
  token: yup.string().required(t("auth.validation.required")),
  confirmPassword: yup
    .string()
    .typeError(t("auth.validation.passwordNotValid"))
    .oneOf([yup.ref("user.password")], t("auth.validation.passwordsMustMatch"))
    .required(t("auth.validation.passwordRequired")),
  user: yup.object({
    password: yup
      .string()
      .typeError(t("auth.validation.passwordNotValid"))
      .min(8, t("auth.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("auth.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("auth.validation.passwordNumberValidation"))
      .required(t("auth.validation.passwordRequired")),
    firstName: yup
      .string()
      .typeError(t("auth.validation.firstNameNotValid"))
      .required(t("auth.validation.firstNameRequired")),
    lastName: yup
      .string()
      .typeError(t("auth.validation.lastNameNotValid"))
      .required(t("auth.validation.lastNameRequired")),
    profilePicture: yup
      .mixed<File | string>()
      .nullable()
      .defined()
      .test(
        "fileType",
        t("auth.validation.profilePictureFormatInvalid", {
          supportedFormats: SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.map(
            (format) => format.label,
          ).join(", "),
        }),
        (value) => {
          if (!value || typeof value === "string") return true;
          return SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.some(
            (format) => format.contentType === value.type,
          );
        },
      )
      .test(
        "fileSize",
        t("auth.validation.profilePictureSizeInvalid", {
          fileSize: MAX_PROFILE_PICTURE_FILE_SIZE / (1024 * 1024),
        }),
        (value) => {
          if (!value || typeof value === "string") return true;
          return value.size <= MAX_PROFILE_PICTURE_FILE_SIZE;
        },
      ),
  }),
  profile: yup.object({
    honorific: yup
      .string()
      .typeError(t("auth.validation.notValid"))
      .oneOf(Honorifics.getValues(), t("auth.validation.notValid"))
      .required(t("auth.validation.required")),
    city: yup
      .string()
      .typeError(t("auth.validation.cityNotValid"))
      .required(t("auth.validation.cityRequired")),
    country: yup
      .number()
      .typeError(t("auth.validation.countryNotValid"))
      .required(t("auth.validation.countryRequired")),
    jobTitle: yup
      .string()
      .typeError(t("auth.validation.jobTitleNotValid"))
      .required(t("auth.validation.jobTitleRequired")),
    department: yup
      .string()
      .typeError(t("auth.validation.departmentNotValid"))
      .required(t("auth.validation.departmentRequired")),
    areasOfExpertise: yup
      .array(
        yup
          .number()
          .typeError(t("auth.validation.areasOfExpertiseNotValid"))
          .required(t("auth.validation.areasOfExpertiseNotValid")),
      )
      .optional(),
  }),
});

export const PERSONAL_DETAILS_VALIDATION_FIELDS: FieldPath<RegisterFormValues>[] =
  [
    "user.password",
    "confirmPassword",
    "user.firstName",
    "user.lastName",
    "profile.honorific",
    "profile.city",
    "profile.country",
    "profile.jobTitle",
    "profile.department",
  ];

export const ORGANIZATION_DETAILS_VALIDATION_FIELDS: FieldPath<AdminRegisterFormValues>[] =
  [
    "organization.country",
    "organization.address",
    "organization.phoneNumber",
    "organization.email",
  ];

export const INVITE_TEAM_VALIDATION_FIELDS: FieldPath<AdminRegisterFormValues>[] =
  ["organization.invites"];

export const registerAccountSchema = (
  t: TFunction,
): yup.ObjectSchema<RegisterFormValues> =>
  yup.object().shape(registerAccountProfileSchema(t));

export const adminInviteRegisterSchema = (
  t: TFunction,
): yup.ObjectSchema<AdminRegisterFormValues> =>
  yup.object().shape({
    ...registerAccountProfileSchema(t),
    organization: yup.object({
      country: yup
        .number()
        .typeError(t("auth.validation.countryNotValid"))
        .required(t("auth.validation.countryRequired")),
      address: yup
        .string()
        .typeError(t("auth.validation.addressNotValid"))
        .required(t("auth.validation.addressRequired")),
      phoneNumber: yup
        .string()
        .typeError(t("auth.validation.phoneNumberNotValid")),
      email: yup
        .string()
        .email(t("auth.validation.emailNotValid"))
        .typeError(t("auth.validation.emailRequired"))
        .required(t("auth.validation.enterValidEmail")),
      invites: yup.array(
        yup
          .string()
          .email(t("auth.validation.enterValidEmail"))
          .required(t("auth.validation.enterValidEmail")),
      ),
    }),
  });
